import React, { useEffect, useMemo, useRef } from "react";
import { Chart, registerables } from "chart.js";
import styles from "./HealingTrajectoryChart.module.scss";

Chart.register(...registerables);

const mcsdBandPlugin = {
  id: "mcsdBand",
  beforeDatasetsDraw(chart, _args, opts) {
    const { bandYMin, bandYMax } = opts;
    if (bandYMin == null || bandYMax == null) return;

    const {
      ctx,
      chartArea: { left, right },
      scales,
    } = chart;
    const yS = scales.y;

    const pxTop = yS.getPixelForValue(Math.max(bandYMin, bandYMax));
    const pxBottom = yS.getPixelForValue(Math.min(bandYMin, bandYMax));

    ctx.save();

    ctx.fillStyle = "rgba(230, 168, 23, 0.06)";
    ctx.fillRect(left, pxTop, right - left, pxBottom - pxTop);

    ctx.beginPath();
    ctx.moveTo(left, pxTop);
    ctx.lineTo(right, pxTop);
    ctx.strokeStyle = "rgba(230, 168, 23, 0.4)";
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 4]);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(left, pxBottom);
    ctx.lineTo(right, pxBottom);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.restore();
  },
};

const valueLabelsPlugin = {
  id: "valueLabels",
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx, scales } = chart;
    const xS = scales.x;
    const yS = scales.y;
    const rows = opts.rows || [];

    ctx.save();
    rows.forEach((row, i) => {
      if (row.y == null) return; 
      const x = xS.getPixelForValue(i);
      const y = yS.getPixelForValue(row.y);

      ctx.font = "600 11px 'Segoe UI', sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = "#333";
      ctx.fillText(Number(row.y).toFixed(1), x, y - 14);

      if (row.isBaseline) {
        ctx.font = "600 10px 'Segoe UI', sans-serif";
        ctx.fillStyle = "#9a7500";
        // ctx.fillText("Stable", x, y + 22);
      } else if (row.pct != null) {
        ctx.font = "600 10px 'Segoe UI', sans-serif";
        ctx.fillStyle = row.pct <= 0 ? "#2e7d32" : "#c62828";
        // ctx.fillText((row.pct > 0 ? "+" : "") + Number(row.pct).toFixed(1) + "%", x, y + 22);
      }
    });
    ctx.restore();
  },
};

const HealingTrajectoryChart = ({ data: dataProp, trendInfo, mcsdAnalysis }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const rows = useMemo(() => {
    const source = dataProp;

    const baseline = Number(source[0]?.y ?? 0);
    return source.map((d, i) => ({
      label: d.x,
      y: isNaN(Number(d.y)) ? null : Number(d.y),
      date: d.date ?? null,
      trend: i === 0 ? trendInfo?.start.y ?? null : i === source?.length - 1 ? trendInfo?.end?.y ?? null : null,
      mscd: d.mscd != null ? Number(d.mscd) : null,
      isBaseline: i === 0,
      pct: i === 0 ? null : +(((Number(d.y) - baseline) / baseline) * 100).toFixed(1),
    }));
  }, [dataProp, trendInfo?.end?.y, trendInfo?.start.y]);

  const mcsdBandYMin = mcsdAnalysis?.y_start;
  const mcsdBandYMax = mcsdAnalysis?.y_end;

  const allYValues = [...rows.map((r) => r.y), ...rows.map((r) => r.trend).filter((v) => v != null)].filter((v) => v != null && !isNaN(v));
  const dataYMax = Math.max(...allYValues);
  const dataYMin = Math.min(...allYValues);

  const yAxisMax = Math.ceil(dataYMax + 2);

  const yAxisMin = Math.max(
    0,
    Math.floor(Math.min(dataYMin, mcsdBandYMin ?? dataYMin) - 2)
  );

  useEffect(() => {
    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const dotFill = rows.map((r) => (r.isBaseline ? "#387ac4" : r.pct <= 0 ? "#15b471" : "#ff4500"));
    const dotBorder = rows.map((r) => (r.isBaseline ? "rgba(56,122,196,0.35)" : r.pct <= 0 ? "rgba(76,175,80,0.35)" : "rgba(229,57,53,0.35)"));

    chartRef.current = new Chart(canvasRef.current, {
      type: "line",
      plugins: [mcsdBandPlugin, valueLabelsPlugin],
      data: {
        labels: rows.map((r) => r.label),
        datasets: [
          {
            label: "Surface Area",
            data: rows.map((r) => r.y),
            borderColor: "#387ac4",
            borderWidth: 1.5,
            pointBackgroundColor: dotFill,
            pointBorderColor: dotBorder,
            pointBorderWidth: 4,
            pointRadius: 3,
            pointHoverRadius: 4,
            tension: 0,
            spanGaps: true,
          },
          {
            label: "Trend",
            data: rows.map((r) => r.trend),
            borderColor: "#ff7300",
            borderWidth: 1.5,
            borderDash: [5, 5],
            pointRadius: 0,
            tension: 0,
            spanGaps: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 30, bottom: 20, left: 10, right: 20 },
        },
        animation: { duration: 700, easing: "easeInOutQuart" },
        plugins: {
          mcsdBand: { bandYMin: mcsdBandYMin, bandYMax: mcsdBandYMax },
          valueLabels: { rows },
          legend: { display: false },
          tooltip: {
            backgroundColor: "#fff",
            borderColor: "#e0e0e0",
            borderWidth: 1,
            titleColor: "#555",
            bodyColor: "#222",
            padding: 10,
            callbacks: {
              title: (tooltipItems) => {
                const row = rows[tooltipItems[0].dataIndex];
                return row.label ?? "";
              },
              label: (ctx) => {
                if (ctx.parsed.y == null) return null;
                const row = rows[ctx.dataIndex];
                if (ctx.datasetIndex === 0) {
                  return [
                    `Area: ${ctx.parsed.y} cm²`,
                    row.date ? `Date: ${row.date}` : null,
                  ].filter(Boolean);
                }
                return `Trend: ${ctx.parsed.y} cm²`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: "#e8e8e8", lineWidth: 1 },
            ticks: { color: "#888", font: { size: 11 }, maxRotation: 0 },
            title: { display: true, text: "Weeks", color: "#666", font: { size: 13 } },
            border: { color: "#ccc" },
          },
          y: {
            min: yAxisMin,
            max: yAxisMax,
            grid: { color: "#e8e8e8", lineWidth: 1 },
            ticks: { color: "#888", font: { size: 11 }, stepSize: 1 },
            title: {
              display: true,
              text: "Surface Area (cm²)",
              color: "#666",
              font: { size: 13 },
            },
            border: { color: "#ccc" },
          },
        },
      },
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [rows, mcsdBandYMin, mcsdBandYMax, yAxisMin, yAxisMax]);

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.chartHeader}>
        <p className={styles.chartTitle}>Wound Healing Trajectory</p>

        <div className={styles.legendRow}>
          <span className={styles.legendItem}>
            <span className={styles.legendLineSolid} />
            Actual
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendLineDash} />
            Trend
          </span>
          <span className={styles.legendItem}>
            <span className={styles.legendBandMcsd} />
            MCSD Zone
          </span>
        </div>
      </div>

      <div className={styles.canvasContainer}>
        <canvas ref={canvasRef} role="img" aria-label="Wound surface area healing trajectory over time" />
      </div>
    </div>
  );
};

export default HealingTrajectoryChart;
